/// <reference path="../../../calculator.d.ts" />

import { routerNavigatedAction } from "@ngrx/router-store";
import { createFeature, createReducer, createSelector, on } from "@ngrx/store";

import { DiscardOpportunityDialogActions } from "../job/job-create/discard-job-component/discard-job.actions";
import { JobFormActionButtonActions } from "../job/job-create/job-form-action-button/job-form-action-button.actions";
import { NavigatorOutboxActions } from "../job/job-navigator/navigator-outbox/navigator-outbox.actions";
import { JobToolSubscriptionActions } from "../job/job-state/job-tool-subscription.actions";
import { JobToolActions } from "../job/job-tool.actions";
import { CALCULATOR_INPUT_METADATA_PREFIX } from "../job/job-tool.reducer";

import { quickJsActions } from "./quickjs.actions";
// import type {} from '../../../../functions/calculator';
// import * as CALCULATOR from '@karve.it/calculator';


export const CALCULATOR_DELIMITER = '-';
const FLAT_CALCULATOR_INPUT_PREFIXES = ['crewPay__', 'fpSplit__'];

type CalculatorInputValue = string | number | boolean | null;
type CalculatorUserInputs = Record<string, CalculatorInputValue>;
type DirtyInputKeys = Record<string, true>;

type QuickJsMetadataAction = {
  job?: { metadata?: Record<string, any> };
  updatedJob?: { metadata?: Record<string, any> };
  output?: { job?: { metadata?: Record<string, any> } };
};

function resolveCalculatorInputsFromMetadata(metadata: Record<string, any> | undefined): CalculatorUserInputs {
  const userInputs: CalculatorUserInputs = {};

  for (const [ key, value ] of Object.entries(metadata || {})) {

    if (!(key.startsWith(CALCULATOR_INPUT_METADATA_PREFIX))) { continue; }

    const properKey = key.slice(CALCULATOR_INPUT_METADATA_PREFIX.length);
    userInputs[properKey] = value as CalculatorInputValue;
  }

  return userInputs;
}

function resolveMetadataFromAction(action: QuickJsMetadataAction): Record<string, any> | undefined {
  if ('job' in action) {
    return action.job?.metadata;
  }

  if ('updatedJob' in action) {
    return action.updatedJob?.metadata;
  }

  if ('output' in action) {
    return action.output?.job?.metadata;
  }

  return undefined;
}

function mergeDirtyUserInputs(params: {
  serverInputs: CalculatorUserInputs;
  currentInputs: CalculatorUserInputs;
  dirtyInputKeys: DirtyInputKeys;
}) {
  const {
    serverInputs,
    currentInputs,
    dirtyInputKeys,
  } = params;
  const mergedInputs: CalculatorUserInputs = {
    ...serverInputs,
  };

  for (const key of Object.keys(dirtyInputKeys)) {
    if (!(key in currentInputs)) { continue; }
    mergedInputs[key] = currentInputs[key];
  }

  return mergedInputs;
}

export const quickJsInitialState = {
  scriptLoaded: false as boolean,
  scriptError: null as string | null,
  elements: [] as FormElement[],
  customerElements: [] as FormElement[],
  userInputs: {} as CalculatorUserInputs,
  dirtyInputKeys: {} as DirtyInputKeys,
  calculatedState: {} as { [ key: string ]: ElementValue },

  navigatorActiveEventId: null as string | null,
  /** Wall-clock ms when the current step was started. Persists across exit/resume. */
  navigatorStepStartTime: null as number | null,
  /** The stepId currently active in the navigator. */
  navigatorCurrentStepId: null as string | null,
  /** Who is on duty today, for the HELP sheet. */
  navigatorOpsManagers: [] as { name: string; phone: string | null }[],
  /**
   * Which event `navigatorOpsManagers` describes.
   *
   * Tracked separately from `navigatorActiveEventId` because that field is **nulled when a run
   * completes** (`navigatorCompleted`), so comparing against it treats a reopen of the same event as a
   * switch to a different one — which threw the list away and defeated the retention below. This
   * is the same modelling error as **#30**: a per-event value has to carry its own event id rather
   * than borrow whichever one happens to be mounted.
   */
  navigatorOpsManagersEventId: null as string | null,
  /**
   * The last refresh of `navigatorOpsManagers` failed, so what is held may be out of date.
   *
   * The list is deliberately **not** cleared on a failed refresh. Reopening the same event keeps
   * the previous list, and clearing it would make the sheet state that nobody is on duty — a
   * confident falsehood, and the crew would lose a number that is almost certainly still correct
   * (same event, same day, same zone). Keeping it and saying so is honest in both directions.
   */
  navigatorOpsManagersStale: false as boolean,
} as const;

type QuickJSState = typeof quickJsInitialState;

export const quickjsFeature = createFeature({
  name: 'Quick JS',
  extraSelectors: () => {

    const selectUnflattenedInputs = createSelector(
      (state: Record<string, any>) => (state['Quick JS'] as QuickJSState)?.userInputs ?? {},
      (userInputs) => {

      const result: any = {};

      for (const key in (userInputs ?? {})) {

        const value = userInputs[key];

        // Crew-pay keys are calculator-owned flat keys; per-user keys contain UUIDs with hyphens.
        if (FLAT_CALCULATOR_INPUT_PREFIXES.some((prefix) => key.startsWith(prefix))) {
          result[key] = value;
          continue;
        }

        const parts = key.split(CALCULATOR_DELIMITER);

        let current = result;

        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (i === parts.length - 1) {
            current[part] = value;
          } else {
            current = current[part] ??= {};
          }
        }
      }

      return result;
    });

    return {
      selectUnflattenedInputs,
    }
  },
  reducer: createReducer(
    quickJsInitialState,
    on(quickJsActions.loadScriptSuccess, (state): QuickJSState => {
      return {
        ...state,
        scriptLoaded: true,
        // Clear any earlier fetch/load error once the script actually loads. fetchScript can
        // emit a transient "No URL found" during the initial-load race (an empty-URL emission
        // slips past its `jobLoaded || !jobId` filter before the job/config populate); without
        // this, that stale error banner sticks on top of an otherwise-working calculator.
        scriptError: null,
      }
    }),
    on(
      quickJsActions.unloadScriptSuccess,
      quickJsActions.unloadScriptError,
      (state): QuickJSState => {
        // Reset calculator state but preserve navigator session state so
        // step restore survives a script load failure (e.g. CORS error).
        return {
          ...quickJsInitialState,
          scriptError: state.scriptError,
          // The inputs are the job's, not the script's. The fetch error already cleared them; this
          // unload is queued behind it and can land after the job has loaded them again, so
          // clearing them here would wipe the saved inputs and miscalculate every figure.
          userInputs: state.userInputs,
          dirtyInputKeys: state.dirtyInputKeys,
          navigatorCurrentStepId: state.navigatorCurrentStepId,
          navigatorStepStartTime: state.navigatorStepStartTime,
          navigatorActiveEventId: state.navigatorActiveEventId,
          // The duty list belongs to the event, not to the script. Dropping it here turned a
          // script reload failure into a HELP sheet that said nobody was on duty.
          navigatorOpsManagers: state.navigatorOpsManagers,
          navigatorOpsManagersEventId: state.navigatorOpsManagersEventId,
          navigatorOpsManagersStale: state.navigatorOpsManagersStale,
        };
      },
    ),
    on(
      quickJsActions.loadScriptError,
      quickJsActions.fetchScriptError,
      (state, { error }): QuickJSState => {
        return {
          ...quickJsInitialState,
          scriptError: error?.message ?? 'Failed to load calculator',
          navigatorCurrentStepId: state.navigatorCurrentStepId,
          navigatorStepStartTime: state.navigatorStepStartTime,
          navigatorActiveEventId: state.navigatorActiveEventId,
          navigatorOpsManagers: state.navigatorOpsManagers,
          navigatorOpsManagersEventId: state.navigatorOpsManagersEventId,
          navigatorOpsManagersStale: state.navigatorOpsManagersStale,
        };
      },
    ),
    on(quickJsActions.inputChanged, (state, { key, value }): QuickJSState => {
      const currentValue = state.userInputs[key];
      const changed = value !== currentValue;
      if (!changed) { return state }

      return {
        ...state,
        userInputs: {
          ...state.userInputs,
          [key]: value,
        },
        dirtyInputKeys: {
          ...state.dirtyInputKeys,
          [key]: true,
        },
      }
    }),
    on(quickJsActions.inputsChanged, (state, { inputs }): QuickJSState => {
      const userInputs = { ...state.userInputs };
      const dirtyInputKeys = { ...state.dirtyInputKeys };
      let changed = false;
      for (const { key, value } of inputs) {
        if (value === userInputs[key]) { continue; }
        userInputs[key] = value;
        dirtyInputKeys[key] = true;
        changed = true;
      }
      if (!changed) { return state; }
      return { ...state, userInputs, dirtyInputKeys };
    }),
    on(quickJsActions.inputReset, (state, { key }): QuickJSState => {
      const userInputs = { ...state.userInputs };
      userInputs[key] = null;

      return {
        ...state,
        userInputs,
        dirtyInputKeys: {
          ...state.dirtyInputKeys,
          [key]: true,
        },
      }
    }),
    on(quickJsActions.runCalculationsSuccess, (state, { elements, customerElements, calculatedState }): QuickJSState => {
      return {
        ...state,
        elements,
        customerElements,
        calculatedState,
      }
    }),
    on(quickJsActions.runCalculationsError, (state): QuickJSState => {
      return {
        ...state,
        userInputs: quickJsInitialState.userInputs,
        dirtyInputKeys: quickJsInitialState.dirtyInputKeys,
      }
    }),
    on(quickJsActions.navigatorStepConfirmed, (state, { stepId, stepStartTime }): QuickJSState => {
      return {
        ...state,
        navigatorCurrentStepId: stepId,
        navigatorStepStartTime: stepStartTime,
        userInputs: {
          ...state.userInputs,
          currentStepId: stepId,
        },
      };
    }),
    // A one-shot resume at mount, not a running correction.
    //
    // This used to be dispatched from a subscription over `selectJob` that re-derived the step on
    // every job change, which is what #46 turned out to be. The guard that used to live here — an
    // in-flight-advance check — was machinery compensating for that, and came out with it. The
    // page is now responsible for dispatching this at most twice per mount (an immediate derive,
    // and one correction if the first server response arrives afterwards); once the crew has moved
    // themselves, nothing re-derives.
    //
    // External moves after mount go through `navigatorStepOverridden`, which is explicit.
    on(quickJsActions.navigatorStateRestored, (state, { stepId, stepStartTime }): QuickJSState => {
      return {
        ...state,
        navigatorCurrentStepId: stepId,
        navigatorStepStartTime: stepStartTime,
        userInputs: {
          ...state.userInputs,
          currentStepId: stepId,
        },
      };
    }),
    // An external authority (ops, or another device) has moved the crew. Authoritative by
    // definition — no guard, no recency test.
    //
    // Scoped to the mounted event: the slice holds one step for the whole session, so an override
    // for a different event must not land here (#30). Silently ignoring it is correct — the run it
    // was aimed at is not on screen.
    on(quickJsActions.navigatorStepOverridden, (state, { eventId, stepId, stepStartTime }): QuickJSState => {
      if (state.navigatorActiveEventId !== eventId) { return state; }

      return {
        ...state,
        navigatorCurrentStepId: stepId,
        navigatorStepStartTime: stepStartTime,
        userInputs: {
          ...state.userInputs,
          currentStepId: stepId,
        },
      };
    }),
    // The server dropped a crew save because ops had moved the crew first (#86b): the crew goes where
    // ops put them, as for any override. The step's own start, so its timer does not restart. A save
    // dropped by a reset is handled on the page, which takes the crew out of the wiped run.
    on(NavigatorOutboxActions.saveDropped, (state, { eventId, reason, currentStep, navigatorData }): QuickJSState => {
      if (reason !== 'moved' || !currentStep || state.navigatorActiveEventId !== eventId) { return state; }

      let run: { stepStartTime?: number; steps?: Record<string, { startedAt?: number | null }> } = {};
      try { run = navigatorData ? JSON.parse(navigatorData) : {}; } catch { /* keep the current start */ }
      const startedAt = run.steps?.[currentStep]?.startedAt ?? run.stepStartTime ?? state.navigatorStepStartTime;

      return {
        ...state,
        navigatorCurrentStepId: currentStep,
        navigatorStepStartTime: startedAt,
        userInputs: {
          ...state.userInputs,
          currentStepId: currentStep,
        },
      };
    }),
    on(quickJsActions.navigatorOpenedForEvent, (state, { eventId }): QuickJSState => {
      // Same event as last time: keep the step state so a resume still works.
      if (state.navigatorActiveEventId === eventId) {
        return { ...state, navigatorActiveEventId: eventId };
      }

      // Different event (or the first open of the session): drop the previous event's step so
      // it cannot seed this run. The page's own restore dispatch lands after this and repopulates
      // it correctly for this event, if there is anything to restore.
      return {
        ...state,
        navigatorActiveEventId: eventId,
        navigatorCurrentStepId: null,
        navigatorStepStartTime: null,
        // A different event can be a different zone and a different day, so last run's duty list
        // must not carry over — the effect refetches for this event. The staleness flag goes with
        // it: there is nothing held to be stale about.
        //
        // Keyed on `navigatorOpsManagersEventId`, NOT on the branch this code sits in. Reaching
        // here does not mean the event changed — `navigatorCompleted` nulls `navigatorActiveEventId`,
        // so reopening the SAME event lands here too, and clearing then would drop a list that is
        // still correct just as the refetch starts.
        ...(state.navigatorOpsManagersEventId === eventId ? {} : {
          navigatorOpsManagers: [],
          navigatorOpsManagersEventId: null,
          navigatorOpsManagersStale: false,
        }),
        userInputs: {
          ...state.userInputs,
          currentStepId: null,
        },
      };
    }),
    on(quickJsActions.opsManagersLoaded, (state, { eventId, opsManagers }): QuickJSState => ({
      ...state,
      navigatorOpsManagers: opsManagers,
      navigatorOpsManagersEventId: eventId,
      navigatorOpsManagersStale: false,
    })),
    // A failed refresh keeps the list and flags it. See `navigatorOpsManagersStale`.
    //
    // Scoped to the event the list is actually for: a late failure from a previous event must not
    // mark the current event's freshly loaded list as stale.
    on(quickJsActions.opsManagersLoadError, (state, { eventId }): QuickJSState => (
      state.navigatorOpsManagersEventId && state.navigatorOpsManagersEventId !== eventId
        ? state
        : { ...state, navigatorOpsManagersStale: true }
    )),
    on(quickJsActions.navigatorReset, (state, { eventId }): QuickJSState => {
      // Only clear if this slice is actually holding state for the event being reset —
      // resetting one event must not wipe a run in progress on another.
      if (state.navigatorActiveEventId && state.navigatorActiveEventId !== eventId) {
        return state;
      }

      return {
        ...state,
        navigatorCurrentStepId: null,
        navigatorStepStartTime: null,
        userInputs: {
          ...state.userInputs,
          currentStepId: null,
        },
      };
    }),
    on(quickJsActions.navigatorCompleted, (state): QuickJSState => {
      // Clear navigator state — the job is done
      return {
        ...state,
        navigatorCurrentStepId: 'completed',
        navigatorActiveEventId: null,
      };
    }),
    // When the estimator discards unsaved changes, clear all calculator inputs
    // including transient discount state (discountPercent, discountApplied).
    on(DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked, (): QuickJSState => ({
      ...quickJsInitialState,
    })),
    // When navigating to the create-opportunity route, clear all calculator
    // inputs, mirroring the reset pattern used by the other job features.
    // Deliberately keeps scriptLoaded/elements: the script stays loaded across
    // navigation, and the cleared inputs are reflected in the elements by the
    // recalculation the runCalculations effect triggers on this navigation.
    on(routerNavigatedAction, (state, { payload: { event: { urlAfterRedirects } } }): QuickJSState => {
      if (urlAfterRedirects.startsWith('/jobs/new')) {
        return {
          ...state,
          userInputs: quickJsInitialState.userInputs,
          dirtyInputKeys: quickJsInitialState.dirtyInputKeys,
        };
      }
      return state;
    }),
    on(
      JobToolActions.jobLoaded,
      JobToolActions.jobDataReset,
      JobToolActions.saveChangesSuccess,
      JobFormActionButtonActions.updateJobSuccess,
      (state, action: QuickJsMetadataAction): QuickJSState => {
        const metadata = resolveMetadataFromAction(action);

        return {
          ...state,
          userInputs: resolveCalculatorInputsFromMetadata(metadata),
          dirtyInputKeys: quickJsInitialState.dirtyInputKeys,
        }
      },
    ),
    // Requirement: `#6951` - the saved boxes are no longer dirty, so later server updates to them
    // merge in again. A box toggled again while the save was in flight stays dirty.
    on(quickJsActions.saveSharedCrewPaySuccess, (state, { inputs }): QuickJSState => {
      const dirtyInputKeys = { ...state.dirtyInputKeys };
      for (const [ key, value ] of Object.entries(inputs)) {
        if (state.userInputs[key] === value) {
          delete dirtyInputKeys[key];
        }
      }

      return { ...state, dirtyInputKeys };
    }),
    on(
      JobToolSubscriptionActions.remoteJobUpdated,
      JobToolSubscriptionActions.localJobUpdated,
      (state, action: QuickJsMetadataAction): QuickJSState => {
        const metadata = resolveMetadataFromAction(action);
        if (!metadata) { return state; }

        const serverInputs = resolveCalculatorInputsFromMetadata(metadata);
        if (Object.keys(serverInputs).length === 0) { return state; }

        return {
          ...state,
          userInputs: mergeDirtyUserInputs({
            serverInputs,
            currentInputs: state.userInputs,
            dirtyInputKeys: state.dirtyInputKeys,
          }),
        };
      },
    ),
  ),
});

// The feature name 'Quick JS' contains a space, which prevents NgRx from generating
// valid TypeScript selector identifiers. We add them explicitly here.
const _selectQuickJsState = (state: Record<string, any>): QuickJSState =>
  state['Quick JS'] as QuickJSState;

export const selectUserInputs = createSelector(
  _selectQuickJsState,
  (s) => s.userInputs,
);

export const selectNavigatorActiveEventId = createSelector(
  _selectQuickJsState,
  (s) => s.navigatorActiveEventId,
);

export const selectNavigatorOpsManagers = createSelector(
  _selectQuickJsState,
  (s) => s.navigatorOpsManagers,
);

export const selectNavigatorOpsManagersStale = createSelector(
  _selectQuickJsState,
  (s) => s.navigatorOpsManagersStale,
);

export const selectNavigatorCurrentStepId = createSelector(
  _selectQuickJsState,
  (s) => s.navigatorCurrentStepId,
);

export const selectNavigatorStepStartTime = createSelector(
  _selectQuickJsState,
  (s) => s.navigatorStepStartTime,
);

export const selectScriptLoaded = createSelector(
  _selectQuickJsState,
  (s) => s.scriptLoaded,
);

export const selectCalculatedState = createSelector(
  _selectQuickJsState,
  (s) => s.calculatedState,
);

export const selectElements = createSelector(
  _selectQuickJsState,
  (s) => s.elements,
);

